import React, { FC, MouseEvent, useState } from "react";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import { servicesSelectAddonsAll } from "../../../../reduxFolder/selectors/servicesSelectors";
import { getAddonsIconsImage } from "../../../Delivery/deliveryHelper";
import {
  RedirectTokenLink,
  RightArrowIcon,
} from "../../../Storage/components/StorageList/StorageList.styles";
import useInventoryItem, { InventoryItemChange } from "../useInventoryItem";
import {
  BackButton,
  DownArrowIcon,
  StyledInput,
  UpArrowIcon,
} from "../../styles/Inventory.style";
import {
  BadgePhotosIcon,
  TileBody,
  TileCheckboxOverlay,
  TileCommentIcon,
  TileContainer,
  TileDescriptionRow,
  TileDescriptionText,
  TileMeta,
  TilePhoto,
  TilePhotoArea,
  TilePhotoCountBadge,
  TilePhotoPlaceholder,
  TileQuantityRow,
  TileWeight,
} from "./InventoryItemTile.styles";

/**
 * Photo-first card variant of InventoryItemRow, rendered by InventoryList when
 * the Storage Items view is in "tiles" mode. Selection, quantity and derived
 * fields come from the shared useInventoryItem hook (also used by the row).
 * Deliberate differences from the row: no location column, the consolidation
 * link is a compact PK token in the meta line (its "Sent in" label moves to the
 * tooltip), and photos cycle in-card via the count badge instead of
 * ImageViewer2.
 */
interface InventoryItemTileProps {
  units: string;
  isSelected: boolean;
  addons: any;
  onChange: (change: InventoryItemChange) => void;
  selectedInventoryItem: any;
  inventoryItem: any;
  isItemInCart: any;
  /** Unused, as in InventoryItemRow — kept so both components share one prop
   * shape (InventoryList swaps one for the other). */
  disabledParent: boolean;
  onFocusInventoryItemInPanel: (item: any) => void;
}

const InventoryItemTile: FC<InventoryItemTileProps> = React.memo(
  ({
    units,
    addons,
    inventoryItem,
    selectedInventoryItem,
    isItemInCart,
    isSelected,
    onChange,
    onFocusInventoryItemInPanel,
  }) => {
    const { t } = useTranslation("common");
    const [pictureIndex, setPictureIndex] = useState(0);
    const {
      item,
      pictures,
      hasPictures,
      weight,
      comment,
      itemAddons,
      parcelDate,
      isDecantItem,
      isCartableItem,
      quantity,
      hasQuantity,
      consolidationLink,
      selectedQuantity,
      handleCheckBoxClick,
      handleCheckboxAreaClick,
      handleQuantityChange,
      handleIncrease,
      handleDecrease,
      preventInputEvents,
    } = useInventoryItem({ inventoryItem, isItemInCart, isSelected, onChange });

    // `pictures` is normalizeImages output — already `{ large, medium }` with
    // the CDN rewrite applied (reduxFolder/reducers/utils/helpers.js). The
    // modulo keeps the index valid if the pictures list refreshes underneath.
    const currentPictureIndex = hasPictures ? pictureIndex % pictures.length : 0;
    const pictureUrl = hasPictures
      ? pictures[currentPictureIndex].medium
      : undefined;

    const handleCyclePicture = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      setPictureIndex((prevIndex) => (prevIndex + 1) % pictures.length);
    };

    return (
      <TileContainer
        role="listitem"
        onClick={() => onFocusInventoryItemInPanel(inventoryItem)}
        $selected={selectedInventoryItem === inventoryItem.get("id")}
        $checked={isSelected || isItemInCart}
      >
        <TilePhotoArea>
          {pictureUrl ? (
            <TilePhoto
              src={pictureUrl}
              alt={item.description || ""}
              loading="lazy"
            />
          ) : (
            <TilePhotoPlaceholder />
          )}
          <TileCheckboxOverlay onClick={handleCheckboxAreaClick}>
            <Checkbox
              checked={isSelected || isItemInCart}
              onChange={handleCheckBoxClick}
              disabled={isItemInCart || !isCartableItem}
            />
          </TileCheckboxOverlay>
          {hasPictures && pictures.length > 1 && (
            <TilePhotoCountBadge
              type="button"
              aria-label={t("parcels.itemsView.nextPhoto")}
              title={t("parcels.itemsView.nextPhoto")}
              onClick={handleCyclePicture}
            >
              <BadgePhotosIcon type={IconType.AdditionalPhotos} />
              {`${currentPictureIndex + 1}/${pictures.length}`}
            </TilePhotoCountBadge>
          )}
        </TilePhotoArea>
        <TileBody>
          <TileDescriptionRow>
            <CopyToClipboard text={item.description ?? ""}>
              <TileDescriptionText title={item.description ?? ""}>
                {item.description}
              </TileDescriptionText>
            </CopyToClipboard>
            {!itemAddons.isEmpty() && getAddonsIconsImage(itemAddons, addons)}
            {comment && (
              <span title={comment}>
                <TileCommentIcon type={IconType.Comment} />
              </span>
            )}
          </TileDescriptionRow>
          {hasQuantity && (
            <TileQuantityRow>
              {isDecantItem && (
                <div>{`${quantity} ${t("parcels.quantity").toLowerCase()}`}</div>
              )}
              {!isDecantItem && (
                <>
                  <BackButton
                    color={ButtonColor.Black50}
                    onClick={handleDecrease}
                    disabled={selectedQuantity === 1}
                  >
                    <DownArrowIcon type={IconType.Arrow} />
                  </BackButton>
                  <StyledInput
                    name="decantQuantity"
                    value={selectedQuantity}
                    onChange={handleQuantityChange}
                    onClick={preventInputEvents}
                  />
                  <BackButton
                    color={ButtonColor.Black50}
                    onClick={handleIncrease}
                    disabled={selectedQuantity === quantity}
                  >
                    <UpArrowIcon type={IconType.Arrow} />
                  </BackButton>
                  <span>{`/ ${quantity}`}</span>
                </>
              )}
            </TileQuantityRow>
          )}
          <TileMeta>
            {!!weight && (
              <TileWeight>
                {`${weight} ${t(`units.${units}.primaryShort`)}`}
              </TileWeight>
            )}
            {item.color && <span>{item.color}</span>}
            {item.size && <span>{item.size}</span>}
            <span>{parcelDate}</span>
            {consolidationLink && (
              <RedirectTokenLink
                to={consolidationLink.to}
                state={consolidationLink.state}
                title={`${t("common.sentIn")} ${consolidationLink.sku}`}
                onClick={(event) => event.stopPropagation()}
              >
                <span>{consolidationLink.sku}</span>
                <RightArrowIcon type={IconType.RightArrow} />
              </RedirectTokenLink>
            )}
          </TileMeta>
        </TileBody>
      </TileContainer>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    addons: servicesSelectAddonsAll,
  }),
);

export default withConnect(InventoryItemTile);
